import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { AppointmentCreatePage } from '../features/appointments/AppointmentCreatePage';
import { AppointmentEditPage } from '../features/appointments/AppointmentEditPage';
import { AppointmentsPage, TransportAppointmentsPage } from '../features/appointments/AppointmentsPage';
import { AuthProvider } from '../features/auth/AuthProvider';
import { useAuth } from '../features/auth/AuthProvider';
import { AuthCallbackPage } from '../features/auth/AuthCallbackPage';
import { RequireAuth } from '../features/auth/RequireAuth';
import { ClientDogsPage } from '../features/client-dogs/ClientDogsPage';
import { OperationsCalendarPage } from '../features/calendar/OperationsCalendarPage';
import { CustomerCreatePage } from '../features/customers/CustomerCreatePage';
import { CustomerEditPage } from '../features/customers/CustomerEditPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { DashboardActivityPage } from '../features/dashboard/DashboardActivityPage';
import { DashboardAlertsPage } from '../features/dashboard/DashboardAlertsPage';
import { DaycareCreatePage } from '../features/daycares/DaycareCreatePage';
import { DaycareEditPage } from '../features/daycares/DaycareEditPage';
import { DaycarePhotosPage } from '../features/daycares/DaycarePhotosPage';
import { DaycaresPage } from '../features/daycares/DaycaresPage';
import { DogCreatePage } from '../features/dogs/DogCreatePage';
import { DogEditPage } from '../features/dogs/DogEditPage';
import { LandingPage } from '../features/landing/LandingPage';
import { MembershipsPage } from '../features/memberships/MembershipsPage';
import { PaymentsListPage } from '../features/payments/PaymentsListPage';
import { PaymentsPage } from '../features/payments/PaymentsPage';
import { PricesPage } from '../features/prices/PricesPage';
import { PhotoCodeLookupPage } from '../features/public/PhotoCodeLookup';
import { PublicPhotoViewPage } from '../features/public/PublicPhotoViewPage';
import { PublicDaycareGalleryPage, PublicStayGalleryPage } from '../features/public/PublicStayGalleryPage';
import { ServiceCreatePage } from '../features/services/ServiceCreatePage';
import { ServiceEditPage } from '../features/services/ServiceEditPage';
import { ServicesPage } from '../features/services/ServicesPage';
import { StayCreatePage } from '../features/stays/StayCreatePage';
import { StayEditPage } from '../features/stays/StayEditPage';
import { StayPhotosPage } from '../features/stays/StayPhotosPage';
import { StaysPage } from '../features/stays/StaysPage';
import { UserCreatePage } from '../features/users/UserCreatePage';
import { UserEditPage } from '../features/users/UserEditPage';
import { RoleCreatePage } from '../features/users/RoleCreatePage';
import { RolePermissionsPage } from '../features/users/RolePermissionsPage';
import { UsersPage } from '../features/users/UsersPage';
import { ErrorState } from '../components/ui/state';
import { AppRouteError } from '../components/ui/route-error';
import { AppErrorBoundary, GlobalErrorMonitor } from '../components/ui/app-error-boundary';
import { ApiRateLimitSnackbar } from '../components/ui/api-rate-limit-snackbar';
import { api } from '../shared/api';
import { shouldRetryApiQuery } from '../shared/api-rate-limit';
import { canAccessModule, defaultModulePermissions, normalizeModulePermissions, type ModuleId, type StaffRole } from '../shared/modulePermissions';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryApiQuery,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createBrowserRouter([
  { path: '/', element: <LandingPage />, errorElement: <AppRouteError /> },
  { path: '/photos', element: <PhotoCodeLookupPage />, errorElement: <AppRouteError /> },
  { path: '/photo-view/:kind/:code/:photoId', element: <PublicPhotoViewPage />, errorElement: <AppRouteError /> },
  { path: '/gallery/:shortId', element: <PublicStayGalleryPage />, errorElement: <AppRouteError /> },
  { path: '/daycare-gallery/:daycareId', element: <PublicDaycareGalleryPage />, errorElement: <AppRouteError /> },
  { path: '/auth/callback', element: <AuthCallbackPage />, errorElement: <AppRouteError /> },
  {
    path: '/admin',
    errorElement: <AppRouteError />,
    element: (
      <AppErrorBoundary area="access" variant="page">
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      </AppErrorBoundary>
    ),
    children: [
      {
        errorElement: <AppRouteError embedded />,
        children: [
          { index: true, element: <Navigate to="/admin/dashboard" replace /> },
          { path: 'dashboard', element: <RequireModule moduleId="dashboard"><DashboardPage /></RequireModule> },
          { path: 'dashboard/activity', element: <RequireModule moduleId="dashboard"><DashboardActivityPage /></RequireModule> },
          { path: 'dashboard/alerts', element: <RequireModule moduleId="dashboard"><DashboardAlertsPage /></RequireModule> },
          { path: 'calendar', element: <RequireModule moduleId="calendar"><OperationsCalendarPage /></RequireModule> },
          { path: 'grooming', element: <RequireModule moduleId="grooming"><AppointmentsPage /></RequireModule> },
          { path: 'transportation', element: <RequireModule moduleId="grooming"><TransportAppointmentsPage /></RequireModule> },
          { path: 'grooming/new', element: <RequireModule moduleId="grooming"><AppointmentCreatePage /></RequireModule> },
          { path: 'grooming/:appointmentId/edit', element: <RequireModule moduleId="grooming"><AppointmentEditPage /></RequireModule> },
          { path: 'appointments', element: <Navigate to="/admin/grooming" replace /> },
          { path: 'customers', element: <Navigate to="/admin/client-dogs" replace /> },
          { path: 'client-dogs', element: <RequireModule moduleId="client-dogs"><ClientDogsPage /></RequireModule> },
          { path: 'customers/new', element: <RequireModule moduleId="client-dogs"><CustomerCreatePage /></RequireModule> },
          { path: 'customers/:customerId/edit', element: <RequireModule moduleId="client-dogs"><CustomerEditPage /></RequireModule> },
          { path: 'dogs', element: <Navigate to="/admin/client-dogs" replace /> },
          { path: 'dogs/new', element: <RequireModule moduleId="client-dogs"><DogCreatePage /></RequireModule> },
          { path: 'dogs/:dogId/edit', element: <RequireModule moduleId="client-dogs"><DogEditPage /></RequireModule> },
          { path: 'stays', element: <RequireModule moduleId="stays"><StaysPage /></RequireModule> },
          { path: 'stays/new', element: <RequireModule moduleId="stays"><StayCreatePage /></RequireModule> },
          { path: 'stays/:stayId/edit', element: <RequireModule moduleId="stays"><StayEditPage /></RequireModule> },
          { path: 'stays/:stayId/photos', element: <RequireModule moduleId="stays"><StayPhotosPage /></RequireModule> },
          { path: 'daycares', element: <RequireModule moduleId="daycares"><DaycaresPage /></RequireModule> },
          { path: 'daycares/new', element: <RequireModule moduleId="daycares"><DaycareCreatePage /></RequireModule> },
          { path: 'daycares/:daycareId/edit', element: <RequireModule moduleId="daycares"><DaycareEditPage /></RequireModule> },
          { path: 'daycares/:daycareId/photos', element: <RequireModule moduleId="daycares"><DaycarePhotosPage /></RequireModule> },
          { path: 'memberships', element: <RequireModule moduleId="memberships"><MembershipsPage /></RequireModule> },
          { path: 'services', element: <RequireModule moduleId="services"><ServicesPage /></RequireModule> },
          { path: 'services/new', element: <RequireRole roles={['Administrator']}><ServiceCreatePage /></RequireRole> },
          { path: 'services/:serviceId/edit', element: <RequireRole roles={['Administrator']}><ServiceEditPage /></RequireRole> },
          { path: 'prices', element: <RequireModule moduleId="prices"><PricesPage /></RequireModule> },
          { path: 'payments', element: <RequireModule moduleId="payments"><PaymentsPage /></RequireModule> },
          { path: 'payments/history', element: <RequireModule moduleId="payments"><PaymentsListPage /></RequireModule> },
          { path: 'users', element: <RequireModule moduleId="users"><UsersPage /></RequireModule> },
          { path: 'users/permissions', element: <RequireModule moduleId="users"><RolePermissionsPage /></RequireModule> },
          { path: 'users/roles/new', element: <RequireRole roles={['Administrator']}><RoleCreatePage /></RequireRole> },
          { path: 'users/new', element: <RequireRole roles={['Administrator']}><UserCreatePage /></RequireRole> },
          { path: 'users/:username/edit', element: <RequireRole roles={['Administrator']}><UserEditPage /></RequireRole> },
        ],
      },
    ],
  },
  { path: '/dashboard', element: <Navigate to="/admin/dashboard" replace /> },
  { path: '/calendar', element: <Navigate to="/admin/calendar" replace /> },
  { path: '/appointments', element: <Navigate to="/admin/grooming" replace /> },
  { path: '/grooming', element: <Navigate to="/admin/grooming" replace /> },
  { path: '/transportation', element: <Navigate to="/admin/transportation" replace /> },
  { path: '/customers', element: <Navigate to="/admin/client-dogs" replace /> },
  { path: '/client-dogs', element: <Navigate to="/admin/client-dogs" replace /> },
  { path: '/dogs', element: <Navigate to="/admin/client-dogs" replace /> },
  { path: '/stays', element: <Navigate to="/admin/stays" replace /> },
  { path: '/daycares', element: <Navigate to="/admin/daycares" replace /> },
  { path: '/memberships', element: <Navigate to="/admin/memberships" replace /> },
  { path: '/services', element: <Navigate to="/admin/services" replace /> },
  { path: '/prices', element: <Navigate to="/admin/prices" replace /> },
  { path: '/payments', element: <Navigate to="/admin/payments" replace /> },
  { path: '/payments/history', element: <Navigate to="/admin/payments/history" replace /> },
  { path: '/users', element: <Navigate to="/admin/users" replace /> },
]);

function RequireRole({ roles, children }: { roles: StaffRole[]; children: ReactNode }) {
  const { user } = useAuth();
  const userRoles = (user?.groups ?? []) as StaffRole[];
  if (roles.some((role) => userRoles.includes(role))) return children;

  const fallback = defaultPathForRoles(userRoles);
  if (fallback) return <Navigate to={fallback} replace />;
  return <ErrorState message="No tienes permisos para ver esta página." />;
}

function RequireModule({ moduleId, children }: { moduleId: ModuleId; children: ReactNode }) {
  const { user } = useAuth();
  const userRoles = (user?.groups ?? []) as StaffRole[];
  const permissionsQuery = useQuery({ queryKey: ['staff-role-permissions'], queryFn: api.staffRolePermissions.get, enabled: Boolean(user) });
  const permissions = normalizeModulePermissions(permissionsQuery.data?.permissions ?? defaultModulePermissions());
  if (canAccessModule(userRoles, moduleId, permissions)) return children;

  const fallback = defaultPathForRoles(userRoles);
  if (fallback) return <Navigate to={fallback} replace />;
  return <ErrorState message="No tienes permisos para ver esta página." />;
}

function defaultPathForRoles(roles: StaffRole[]) {
  if (roles.includes('Administrator') || roles.includes('Receptionist')) return '/admin/dashboard';
  if (roles.includes('Staff')) return '/admin/grooming';
  return '';
}

export function App() {
  return (
    <AppErrorBoundary area="application" variant="page">
      <GlobalErrorMonitor />
      <ApiRateLimitSnackbar />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
